import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Check, X, Clock, User, UsersThree, Minus, Plus, SpinnerGap, CalendarBlank, MapPinLine, NavigationArrow, HeartStraight } from '@phosphor-icons/react';
import api from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getVenueLocationUrl } from '../../lib/locationLinks';
import {
  formatCompanionSummary,
  getInvitedItineraryEventIds,
  normalizeGuestPartyResponse,
  serializeCompanionCount,
} from './guestPartyUtils';
import InfoTip from '../../components/InfoTip';

const normalizeRsvpStatus = (value) => {
  if (value === 'confirmed' || value === 'declined') return value;
  return 'pending';
};

/**
 * Guest RSVP screen with phone-group awareness.
 *
 * When a guest logs in and multiple guests share their phone number, they
 * must first pick "which one is me" before RSVPing. After that, they can
 * set the total companion count without creating synthetic guest records.
 */
export default function RSVPPage() {
  const { currentEvent, user, refreshCurrentEvent } = useAuth();

  // Phone-group state
  const [groupLoading, setGroupLoading] = useState(true);
  const [phoneGroup, setPhoneGroup] = useState([]);
  const [primaryGuestId, setPrimaryGuestId] = useState(null);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState(null); // radio choice pre-save
  const [savingPrimary, setSavingPrimary] = useState(false);
  const [invitedItineraryEventIds, setInvitedItineraryEventIds] = useState([]);

  // Accompanying count
  const [accompanyingCount, setAccompanyingCount] = useState(0);
  const [minimumAccompanyingCount, setMinimumAccompanyingCount] = useState(0);
  const [usesTotalCompanionContract, setUsesTotalCompanionContract] = useState(true);
  const [savingCount, setSavingCount] = useState(false);

  // RSVP state
  const [rsvps, setRsvps] = useState([]);
  const [rsvpStatusByItemId, setRsvpStatusByItemId] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingRsvpKey, setSavingRsvpKey] = useState(null);
  const [refreshedLegacyItineraryEventId, setRefreshedLegacyItineraryEventId] = useState(null);

  const primaryGuest = useMemo(
    () => phoneGroup.find((g) => g.id === primaryGuestId) || null,
    [phoneGroup, primaryGuestId]
  );

  const eventItinerary = useMemo(
    () => currentEvent?.itinerary || [],
    [currentEvent?.itinerary]
  );

  const siblings = useMemo(
    () => phoneGroup.filter((g) => g.id !== primaryGuestId),
    [phoneGroup, primaryGuestId]
  );

  const companionSummary = useMemo(
    () => formatCompanionSummary(siblings.map((guest) => guest.name), accompanyingCount),
    [siblings, accompanyingCount]
  );

  const totalPartySize = 1 + accompanyingCount;
  const savedAccompanyingCount = useMemo(
    () => Math.max(0, phoneGroup.length - 1) + Number(primaryGuest?.accompanying_guests || 0),
    [phoneGroup.length, primaryGuest?.accompanying_guests]
  );

  const hasLegacyItineraryItems = useMemo(
    () => Boolean(eventItinerary.some((item) => !item?.id)),
    [eventItinerary]
  );

  const clampAccompanyingCount = useCallback((value) => {
    const parsed = Number.parseInt(value, 10);
    return Math.max(minimumAccompanyingCount, Number.isFinite(parsed) ? parsed : minimumAccompanyingCount);
  }, [minimumAccompanyingCount]);

  useEffect(() => {
    if (
      currentEvent?.id
      && hasLegacyItineraryItems
      && refreshedLegacyItineraryEventId !== currentEvent.id
    ) {
      setRefreshedLegacyItineraryEventId(currentEvent.id);
      refreshCurrentEvent();
    }
  }, [currentEvent?.id, hasLegacyItineraryItems, refreshedLegacyItineraryEventId, refreshCurrentEvent]);

  const loadGuestGroup = useCallback(async () => {
    if (!currentEvent?.id) return;
    setGroupLoading(true);
    try {
      const res = await api.get(`/events/${currentEvent.id}/my-guest-group`);
      const party = normalizeGuestPartyResponse(res.data);
      const group = party.group;
      setPhoneGroup(group);
      setPrimaryGuestId(party.primaryGuestId);
      setInvitedItineraryEventIds(getInvitedItineraryEventIds(res.data, group));
      setMinimumAccompanyingCount(party.minimumAccompanyingCount);
      setUsesTotalCompanionContract(party.usesTotalCompanionContract);
      setAccompanyingCount(party.accompanyingCount);
    } catch (err) {
      console.error('Failed to load guest group', err);
    } finally {
      setGroupLoading(false);
    }
  }, [currentEvent?.id]);

  const fetchRsvps = useCallback(async () => {
    if (!currentEvent?.id || !primaryGuestId) return;
    try {
      const rsvpsRes = await api.get(`/events/${currentEvent.id}/rsvp`, {
        params: { guest_id: primaryGuestId }
      });
      const mine = rsvpsRes.data || [];
      setRsvps(mine);
      setRsvpStatusByItemId((current) => {
        const fetchedStatuses = mine.reduce((acc, rsvp) => {
          if (rsvp.itinerary_item_id) {
            acc[rsvp.itinerary_item_id] = normalizeRsvpStatus(rsvp.status);
          }
          return acc;
        }, {});
        return { ...current, ...fetchedStatuses };
      });
    } catch (err) {
      console.error('Failed to fetch RSVPs', err);
    } finally {
      setLoading(false);
    }
  }, [currentEvent?.id, primaryGuestId]);

  useEffect(() => {
    loadGuestGroup();
  }, [loadGuestGroup]);

  useEffect(() => {
    if (primaryGuestId) fetchRsvps();
    else setLoading(false);
  }, [primaryGuestId, fetchRsvps]);

  // --------------- Primary-guest selection --------------------------------
  const handleSavePrimary = async () => {
    if (!selectedPrimaryId) {
      toast.error('Please select which name is you');
      return;
    }
    setSavingPrimary(true);
    try {
      const res = await api.post(
        `/events/${currentEvent.id}/guest-group/select-primary`,
        { guest_id: selectedPrimaryId }
      );
      const party = normalizeGuestPartyResponse(res.data, selectedPrimaryId);
      setPhoneGroup(party.group);
      setPrimaryGuestId(party.primaryGuestId);
      setInvitedItineraryEventIds(getInvitedItineraryEventIds(res.data, party.group));
      setMinimumAccompanyingCount(party.minimumAccompanyingCount);
      setUsesTotalCompanionContract(party.usesTotalCompanionContract);
      setAccompanyingCount(party.accompanyingCount);
      toast.success('Great — you can now RSVP');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save selection');
    } finally {
      setSavingPrimary(false);
    }
  };

  // --------------- Accompanying count -------------------------------------
  const handleSaveAccompanyingCount = async (newCount) => {
    const nextCount = clampAccompanyingCount(newCount);
    if (nextCount < 0 || nextCount > 50) return;
    setSavingCount(true);
    try {
      const res = await api.post(
        `/events/${currentEvent.id}/guest-group/accompanying-count`,
        {
          count: serializeCompanionCount(
            nextCount,
            phoneGroup.length,
            usesTotalCompanionContract
          )
        }
      );
      const party = normalizeGuestPartyResponse(res.data, primaryGuestId);
      setPhoneGroup(party.group);
      setMinimumAccompanyingCount(party.minimumAccompanyingCount);
      setUsesTotalCompanionContract(party.usesTotalCompanionContract);
      setAccompanyingCount(party.accompanyingCount);
      toast.success('Attendee count updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update count');
    } finally {
      setSavingCount(false);
    }
  };

  // --------------- Per-itinerary RSVP -------------------------------------
  const handleRSVP = async (itineraryItemId, status) => {
    if (!primaryGuestId) return;
    if (!invitedItineraryEventIds.includes(itineraryItemId)) {
      toast.error('This celebration is not assigned to your invitation');
      return;
    }
    const saveKey = `${itineraryItemId}:${status}`;
    const previousRsvps = rsvps;
    const previousStatusByItemId = rsvpStatusByItemId;
    setSavingRsvpKey(saveKey);
    setRsvpStatusByItemId((current) => ({
      ...current,
      [itineraryItemId]: status
    }));
    setRsvps((current) => {
      const optimisticRsvp = {
        id: `optimistic-${itineraryItemId}`,
        guest_id: primaryGuestId,
        itinerary_item_id: itineraryItemId,
        status,
      };
      const existingIndex = current.findIndex((r) => r.itinerary_item_id === itineraryItemId);
      if (existingIndex === -1) return [...current, optimisticRsvp];
      return current.map((r, index) => (index === existingIndex ? { ...r, status } : r));
    });

    try {
      const response = await api.post(`/events/${currentEvent.id}/rsvp`, {
        guest_id: primaryGuestId,
        itinerary_item_id: itineraryItemId,
        status,
      });
      setRsvps((current) => {
        const existingIndex = current.findIndex((r) => r.itinerary_item_id === itineraryItemId);
        if (existingIndex === -1) return [...current, response.data];
        return current.map((r, index) => (index === existingIndex ? response.data : r));
      });
      setRsvpStatusByItemId((current) => ({
        ...current,
        [itineraryItemId]: normalizeRsvpStatus(response.data?.status || status)
      }));
      toast.success(`RSVP ${status === 'confirmed' ? 'confirmed' : 'declined'}`);
    } catch (err) {
      console.error('Failed to update RSVP', err);
      setRsvps(previousRsvps);
      setRsvpStatusByItemId(previousStatusByItemId);
      toast.error(err.response?.data?.detail || 'Failed to update RSVP');
    } finally {
      setSavingRsvpKey(null);
    }
  };

  const getRSVPStatus = (itemId) => {
    if (rsvpStatusByItemId[itemId]) {
      return rsvpStatusByItemId[itemId];
    }
    const rsvp = rsvps.find((r) => r.itinerary_item_id === itemId);
    return rsvp?.status || 'pending';
  };

  const getRsvpSavingStatus = (itemId) => {
    if (!savingRsvpKey?.startsWith(`${itemId}:`)) return null;
    return savingRsvpKey.endsWith(':confirmed') ? 'confirmed' : 'declined';
  };

  const getOverallCopy = () => {
    const respondedCount = confirmedCount + declinedCount;
    if (itinerary.length === 0) {
      return {
        title: 'No RSVP moments assigned yet',
        note: 'Your hosts will share RSVP moments here when they are assigned to your invitation.',
      };
    }
    if (pendingCount === 0) {
      return {
        title: 'Your response is complete',
        note: `You have responded to all ${itinerary.length} celebration moments.`,
      };
    }
    if (respondedCount > 0) {
      return {
        title: `You have responded to ${respondedCount} of ${itinerary.length} celebrations`,
        note: 'A few moments still need your reply when you are ready.',
      };
    }
    return {
      title: 'Choose your celebration moments',
      note: "We're delighted to know which moments you will join us for.",
    };
  };

  const getStatusBadge = (status, isSaving = false) => {
    if (isSaving) {
      return (
        <Badge className="border border-wedding-sage/30 bg-wedding-sage/10 text-wedding-sage shadow-sm flex items-center gap-1 px-3 py-1">
          <SpinnerGap className="w-3 h-3 animate-spin" /> Saving...
        </Badge>
      );
    }

    const config = {
      confirmed: { color: 'border-[#b7d8be] bg-[#edf7ef] text-[#386742]', icon: Check, label: 'Will attend' },
      declined: { color: 'border-[#efc9cc] bg-[#fff1f2] text-[#9a4a54]', icon: X, label: 'Regrets sent' },
      pending: { color: 'border-secondary/35 bg-secondary/15 text-foreground', icon: Clock, label: 'Awaiting reply' },
    };
    const { color, icon: Icon, label } = config[normalizeRsvpStatus(status)] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1 rounded-full px-3 py-1 font-medium shadow-none`}>
        <Icon className="w-3 h-3" /> {label}
      </Badge>
    );
  };

  const getRsvpCardClass = (status, isSaving = false) => {
    if (status === 'confirmed') {
      return `${isSaving ? 'ring-2 ring-[#b7d8be]' : 'ring-1 ring-[#d9eadc]'} border-[#cfe4d3] bg-[#f6fbf6] shadow-[0_18px_45px_rgba(67,103,72,0.08)]`;
    }
    if (status === 'declined') {
      return `${isSaving ? 'ring-2 ring-[#efc9cc]' : 'ring-1 ring-[#f3d8db]'} border-[#f0d2d5] bg-[#fff8f8] shadow-[0_18px_45px_rgba(154,74,84,0.07)]`;
    }
    return 'border-border bg-card shadow-[0_14px_35px_rgba(0,0,0,0.05)] hover:border-primary/40';
  };

  const getRsvpAccentClass = (status) => {
    if (status === 'confirmed') return 'bg-[#8fb99a]';
    if (status === 'declined') return 'bg-[#e5aeb3]';
    return 'bg-secondary';
  };

  const getConfirmButtonClass = (status) => (
    status === 'confirmed'
      ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
      : 'text-primary hover:bg-primary/10'
  );

  const getDeclineButtonClass = (status) => (
    status === 'declined'
      ? 'bg-[#b56a73] text-white shadow-sm hover:bg-[#a75d66]'
      : 'text-[#9a4a54] hover:bg-[#fff1f2]'
  );

  const getSummaryCardClass = () => {
    if (itinerary.length === 0) return 'border-border bg-card';
    if (pendingCount === 0) return 'border-[#cfe4d3] bg-[#f6fbf6]';
    if (confirmedCount > 0 || declinedCount > 0) return 'border-primary/30 bg-primary/5';
    return 'border-secondary/35 bg-secondary/10';
  };

  const itinerary = useMemo(
    () => eventItinerary.filter((item) => item?.id && invitedItineraryEventIds.includes(item.id)),
    [eventItinerary, invitedItineraryEventIds]
  );
  const confirmedCount = itinerary.filter((i) => getRSVPStatus(i.id) === 'confirmed').length;
  const declinedCount = itinerary.filter((i) => getRSVPStatus(i.id) === 'declined').length;
  const pendingCount = itinerary.length - confirmedCount - declinedCount;

  // --------------- Render blocking "who are you?" step ---------------------
  const needsPrimarySelection = phoneGroup.length > 1 && !primaryGuestId;

  if (groupLoading || loading) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-muted-foreground">Loading…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-1">
          <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="rsvp-title">RSVP</h1>
          <InfoTip content="Choose the celebration moments you will join." label="About RSVP" />
        </div>

        {needsPrimarySelection && (
          <Card className="border-wedding-gold/50 bg-wedding-gold/5" data-testid="select-primary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-serif">
                <User className="w-5 h-5" /> Which of these is you?
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Multiple guests share your phone number. Pick the one that&apos;s you before you RSVP.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {phoneGroup.map((g) => (
                <label
                  key={g.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                    selectedPrimaryId === g.id
                      ? 'border-wedding-sage bg-wedding-sage/10'
                      : 'border-muted hover:bg-muted/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="primary-guest"
                    value={g.id}
                    checked={selectedPrimaryId === g.id}
                    onChange={() => setSelectedPrimaryId(g.id)}
                    data-testid={`primary-radio-${g.id}`}
                    className="w-4 h-4"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{g.name}</p>
                    {g.email && <p className="text-xs text-muted-foreground">{g.email}</p>}
                  </div>
                  {g.is_admin && <Badge variant="outline">Admin</Badge>}
                </label>
              ))}
              <Button
                onClick={handleSavePrimary}
                disabled={savingPrimary || !selectedPrimaryId}
                className="w-full bg-wedding-sage hover:bg-wedding-sage/90"
                data-testid="save-primary-btn"
              >
                {savingPrimary ? 'Saving…' : "That's me — continue"}
              </Button>
            </CardContent>
          </Card>
        )}

        {!needsPrimarySelection && primaryGuest && (
          <>
            {/* Overall RSVP status */}
            <Card className={`overflow-hidden transition-colors duration-200 ${getSummaryCardClass()}`}>
              <CardContent className="p-5 md:p-7">
                <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-[0.6875rem] uppercase tracking-[0.32em] text-wedding-charcoal/45">RSVP note</p>
                    <p className="mt-2 font-serif text-2xl font-semibold text-wedding-charcoal md:text-3xl">
                      {getOverallCopy().title}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-wedding-charcoal/65">
                      {getOverallCopy().note}
                    </p>
                    {itinerary.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {confirmedCount} joining | {declinedCount} regrets | {pendingCount} awaiting reply
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <span className="rounded-lg border border-[#cfe4d3] bg-white/80 px-3 py-2 text-xs font-semibold text-[#486f50]">
                      <span className="block font-serif text-xl font-normal">{confirmedCount}</span>
                      joining
                    </span>
                    <span className="rounded-lg border border-[#f0d2d5] bg-white/80 px-3 py-2 text-xs font-semibold text-[#9a4a54]">
                      <span className="block font-serif text-xl font-normal">{declinedCount}</span>
                      regrets
                    </span>
                    <span className="rounded-lg border border-secondary/35 bg-card/80 px-3 py-2 text-xs font-semibold text-foreground">
                      <span className="block font-serif text-xl font-normal">{pendingCount}</span>
                      pending
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-[0_14px_35px_rgba(0,0,0,0.05)]" data-testid="accompanying-card">
              <CardContent className="space-y-5 p-5 md:p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-primary/10 text-wedding-sage">
                      <UsersThree className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="font-serif text-xl font-semibold text-wedding-charcoal">
                        Guests with you
                      </h2>
                      {/* <div className="mt-1 flex flex-wrap items-center gap-2">
                        <Label className="text-sm text-wedding-charcoal/70">Accompanying guests</Label>
                        <Badge variant="outline" className="border-border bg-card/80 text-foreground">
                          {totalPartySize} total
                        </Badge>
                      </div> */}
                      <p className="mt-1 text-sm leading-6 text-wedding-charcoal/60">
                        Let the hosts know how many loved ones will accompany you.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-start md:self-auto">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        handleSaveAccompanyingCount(Math.max(minimumAccompanyingCount, accompanyingCount - 1))
                      }
                      disabled={savingCount || accompanyingCount <= minimumAccompanyingCount}
                      data-testid="accompanying-decrement"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <Input
                      type="number"
                      min={minimumAccompanyingCount}
                      max={50}
                      value={accompanyingCount}
                      onChange={(e) =>
                        setAccompanyingCount(clampAccompanyingCount(e.target.value))
                      }
                      onBlur={(e) => {
                        const v = clampAccompanyingCount(e.target.value);
                        if (v !== savedAccompanyingCount) {
                          handleSaveAccompanyingCount(v);
                        }
                      }}
                      className="w-20 text-center"
                      data-testid="accompanying-input"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => handleSaveAccompanyingCount(accompanyingCount + 1)}
                      disabled={savingCount || accompanyingCount >= 50}
                      data-testid="accompanying-increment"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {companionSummary && (
                  <div className="rounded-lg border border-border bg-background/50 p-4">
                    <p className="text-[0.6875rem] uppercase tracking-[0.24em] text-wedding-charcoal/45">
                      Attending with you
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-wedding-charcoal/75">
                      <User className="w-3.5 h-3.5 text-wedding-sage" />
                      <span>{companionSummary}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-3 md:space-y-5">
              <div className="flex items-center gap-3 px-1 py-1">
                <span className="h-px flex-1 bg-border" />
                <HeartStraight className="h-5 w-5 text-secondary" weight="fill" />
                <span className="h-px flex-1 bg-border" />
              </div>
              {itinerary.map((item) => {
                const status = getRSVPStatus(item.id);
                const savingStatus = getRsvpSavingStatus(item.id);
                const isSavingThisItem = Boolean(savingStatus);
                return (
                  <Card
                    key={item.id}
                    className={`relative overflow-hidden transition-all duration-200 ${getRsvpCardClass(status, isSavingThisItem)}`}
                    data-testid={`rsvp-item-${item.id}`}
                  >
                    <div className={`absolute inset-y-0 left-0 w-1.5 ${getRsvpAccentClass(status)}`} />
                    <CardContent className="p-4 pl-6 md:p-6 md:pl-8">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-start gap-2 md:gap-3">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-[0.625rem] uppercase tracking-[0.24em] text-wedding-charcoal/40">
                                Celebration
                              </p>
                                {getStatusBadge(status, isSavingThisItem)}
                              </div>
                              <h3 className="mt-1 font-serif text-xl font-semibold leading-tight text-wedding-charcoal md:text-2xl">
                                {item.name}
                              </h3>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-col gap-1.5 text-sm text-wedding-charcoal/65 md:flex-row md:flex-wrap md:gap-5">
                            <p className="inline-flex items-center gap-2">
                              <CalendarBlank className="h-4 w-4 text-wedding-sage" />
                              {format(new Date(item.date), 'EEEE, MMMM d, yyyy')} at {item.start_time}
                            </p>
                            {item.venue?.name && (
                              getVenueLocationUrl(item.venue) ? (
                                <a
                                  href={getVenueLocationUrl(item.venue)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 font-semibold text-wedding-sage hover:underline"
                                  aria-label={`Open ${item.venue.name} in Google Maps`}
                                >
                                  <NavigationArrow className="h-4 w-4" weight="fill" />
                                  <span>{item.venue.name}{item.venue.hall_name ? `, ${item.venue.hall_name}` : ''}</span>
                                </a>
                              ) : (
                                <p className="inline-flex items-center gap-2">
                                  <MapPinLine className="h-4 w-4 text-wedding-sage" />
                                  <span>{item.venue.name}{item.venue.hall_name ? `, ${item.venue.hall_name}` : ''}</span>
                                </p>
                              )
                            )}
                          </div>
                        </div>
                        <div className="inline-flex w-full rounded-full border border-border bg-card/80 p-0.5 shadow-sm lg:w-auto">
                          <Button
                            type="button"
                            variant="ghost"
                            className={`h-8 flex-1 rounded-full border-0 px-2.5 text-xs font-semibold lg:min-w-[128px] ${getConfirmButtonClass(status)}`}
                            onClick={() => handleRSVP(item.id, 'confirmed')}
                            disabled={Boolean(savingRsvpKey)}
                            aria-pressed={status === 'confirmed'}
                            data-testid={`confirm-${item.id}`}
                          >
                            {savingRsvpKey === `${item.id}:confirmed`
                              ? <SpinnerGap className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <Check className="w-3.5 h-3.5 mr-1" />}
                            {savingRsvpKey === `${item.id}:confirmed`
                              ? 'Saving...'
                              : 'Will attend'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            className={`h-8 flex-1 rounded-full border-0 px-2.5 text-xs font-semibold lg:min-w-[128px] ${getDeclineButtonClass(status)}`}
                            onClick={() => handleRSVP(item.id, 'declined')}
                            disabled={Boolean(savingRsvpKey)}
                            aria-pressed={status === 'declined'}
                            data-testid={`decline-${item.id}`}
                          >
                            {savingRsvpKey === `${item.id}:declined`
                              ? <SpinnerGap className="w-3.5 h-3.5 mr-1 animate-spin" />
                              : <X className="w-3.5 h-3.5 mr-1" />}
                            {savingRsvpKey === `${item.id}:declined`
                              ? 'Saving...'
                              : 'Send regrets'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {itinerary.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <img src="/assets/logo.png" alt="GalaGo logo" className="w-12 h-12 object-contain mx-auto mb-4" />
                    <p className="text-muted-foreground">No RSVP moments are assigned to your invitation yet.</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
